import { useContext, useEffect } from 'react';
import { useLocation } from '@reach/router';
// services
import { useScript } from '@services/useHooks';
import { getCookie, setCookie } from '@services/cookies';
import { setSessionStorageItem } from '../utils/storage';
//context
import { GlobalContext } from '@store/global-state';
//constants
import { AWIN_URL } from '@constants/variables';
import { COOKIE_EXPIRES_IN, COOKIES } from '@constants/cookies';
import { STORAGE_KEYS } from '@constants/storage';
//hooks
import { useLayoutData } from '@hooks/useContent';
import { useCreateGuest } from '@hooks/useCreateGuest';
import { useFeatureFlags } from './useFeatureFlags';
import { useDetectLocale } from './useDetectLocale';
import { useCheckLogInAsync } from './useCheckLogInUser';
import couponCodeService from '@src/utils/couponCodeService';
import { appendStoredTrackingParamsToUrl } from '@src/utils/shopify-tracking-params';

// Redirect to Shopify cart with the given variant ID (stored in sku field)
const redirectToShopifyCart = (variantId: string) => {
  const shopifyHost = process.env.GATSBY_SHOPIFY_HOST || 'vitl.com';
  window.location.href = appendStoredTrackingParamsToUrl(`https://${shopifyHost}/cart/${variantId}:1`);
};

const useRouteActions = () => {
  const route = useLocation().search;

  //functions
  const triggerRouteActions = () => {
    let params = new URLSearchParams(route);

    const isCurrencySet = getCookie(COOKIES.currency);
    const isUserCountrySet = getCookie(COOKIES.userCountry);

    //Workaround to make openBasket work with two question marks routes
    const hasRouteOpenBasket = route.includes('openBasket');

    if (hasRouteOpenBasket) {
      const hasRouteTwoQuestionMarks = route.split('?')[2];

      // Save the utm param in the cookies
      if (hasRouteTwoQuestionMarks) {
        const [utmKey, utmValue] = route.split('?')[2]?.split('&')[0]?.split('=');

        setCookie(utmKey, utmValue, COOKIE_EXPIRES_IN.maxAge);

        if (!isCurrencySet || !isUserCountrySet) {
          setCookie(COOKIES.currency, 'GBP', COOKIE_EXPIRES_IN.thirtyDays);
          setCookie(COOKIES.userCountry, 'GB', COOKIE_EXPIRES_IN.thirtyDays);
        }
      }

      params = new URLSearchParams(route.replace(/\?/g, '&'));
    }

    const routesActions = {
      currency: () => {
        const currency = String(params.get('currency')?.toUpperCase());
        setCookie(COOKIES.currency, currency, COOKIE_EXPIRES_IN.thirtyDays);
      },
      addPlanToCart: () => {
        const sku = params.get('addPlanToCart');
        if (sku) {
          redirectToShopifyCart(sku);
        }
      },
      claimCoupon: () => {
        const { setCoupon } = couponCodeService();
        const couponCode = params.get('claimCoupon');
        if (couponCode) setCoupon(couponCode);
      },
      openBasket: () => {
        // Redirect to Shopify cart page
        const shopifyHost = process.env.GATSBY_SHOPIFY_HOST || 'vitl.com';
        window.location.href = appendStoredTrackingParamsToUrl(`https://${shopifyHost}/cart`);
      },
    };

    for (const [key, value] of Object.entries(routesActions)) {
      if (params.has(key)) value();
    }
  };

  return { triggerRouteActions };
};

const useUpdateAppHeight = () => {
  const { setAppHeight } = useContext(GlobalContext);

  const updateAppHeight = () => {
    setAppHeight(window.innerHeight);
  };

  const addHeightResizeListener = () => {
    window.addEventListener('resize', updateAppHeight);
  };

  const removeHeightResizeListener = () => {
    window.removeEventListener('resize', updateAppHeight);
  };

  return {
    updateAppHeight,
    addHeightResizeListener,
    removeHeightResizeListener,
  };
};

const useUtmModal = () => {
  //context
  const { setUtmModalData } = useContext(GlobalContext);
  //hooks
  const { allDirectusModal } = useLayoutData();

  const setUtmModal = () => {
    // check for UTM modal stored in cookies
    const modalType = getCookie(COOKIES.utm_show_popup);
    const modalData = allDirectusModal.nodes.find((modal: any) => modal.type === modalType);
    const isModalSeen = getCookie('utm-modal-' + modalType);
    if (!isModalSeen && modalData) {
      setUtmModalData(modalData);
    }
  };
  return { setUtmModal };
};

export const useLayout = () => {
  const { allDirectusGenericPage, directusSetting, site } = useLayoutData();
  const {
    showCrossSellSingle,
    messageModal,
    utmModalData,
    showBasket,
    showSearch,
    setShowSearch,
    setShowBasket,
    sidebar,
    showShopModal,
    isCheckLoginTried,
  } = useContext(GlobalContext);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isLandingPage = location.pathname.startsWith('/lp/');
  const hasTokenInParams = params.get('token');
  const traceTokenParamValue = params.get('traceToken');

  //hooks
  const { setUtmModal } = useUtmModal();
  const { triggerRouteActions } = useRouteActions();
  const { startGuestCreate, loading: loadingCreateGuest } = useCreateGuest();
  const { getFeatureFlags, loadingFeatureFlags, dataFeatureFlags } = useFeatureFlags();
  const { detectLocale } = useDetectLocale();
  const { checkLogInUser, checkingLogin, setCheckingLogin } = useCheckLogInAsync();

  const {
    updateAppHeight,
    addHeightResizeListener,
    removeHeightResizeListener,
  } = useUpdateAppHeight();

  useScript(`${AWIN_URL}`); // init AWIN script

  useEffect(() => {
    if (!isLandingPage) {
      if (!isCheckLoginTried) checkLogInUser();
      else setCheckingLogin(false);

      !dataFeatureFlags && getFeatureFlags();
      !hasTokenInParams && startGuestCreate();
    } else {
      setCheckingLogin(false);
    }

    updateAppHeight();
    addHeightResizeListener();
    traceTokenParamValue && setSessionStorageItem(STORAGE_KEYS.traceToken, traceTokenParamValue);
    setUtmModal();

    const isUserCountrySet = getCookie(COOKIES.userCountry);
    const isUserCurrencySet = getCookie(COOKIES.currency);

    if (!isUserCountrySet || !isUserCurrencySet) detectLocale();
    return () => removeHeightResizeListener();
  }, [isCheckLoginTried, isLandingPage]);

  useEffect(() => {
    triggerRouteActions();
  }, []);

  const loading = isLandingPage ? false : loadingCreateGuest || loadingFeatureFlags || checkingLogin;
  return {
    loading,
    site,
    directusSetting,
    allDirectusGenericPage,
    showCrossSellSingle,
    messageModal,
    utmModalData,
    showBasket,
    setShowBasket,
    showSearch,
    setShowSearch,
    sidebar,
    showShopModal,
  };
};
